/*
 * File: POReceiptDataDisplay.tsx
 * Project: mdsi-template-app
 * -----
 * Last Modified: 11 Feb 2021 2:16:23 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Col, DatePicker, Input, message, Modal, Row, Select, Tooltip, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { POHeader } from 'components/atoms/POHeader';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { POReceiptModel } from 'models/POReceiptModel';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { asyncSubmitPackingSlip, removeSelectedRows, removeSelectedSerialNumbers, setClearPOSubmissionData, setSelectedKeysToDelete, setSelectedToDelete } from 'redux/poReceipts/po-receipts-slice';
import { ReduxState } from 'redux/store';

interface Props {
  poReceipt: POReceiptModel;
}

const styles: InlineStylesModel = {
  data: {
    fontWeight: 700,
    paddingLeft: 5
  },
  datePicker: {
    width: '100%',
    marginBottom: 16
  },
  divider: {
    height: 100,
    color: 'grey',
    borderLeft: '1px solid rgba(0, 0, 0, .2)'
    // marginRight: 40
  },
  dividerColumn: {
    textAlign: 'center'
    // marginRight: 40
  },
  mainContainer: {
    padding: '20px 30px',
    backgroundColor: 'white'
  },
  mainColumns: {
    // marginRight: 40
  },
  selectHeader: {
    paddingBottom: 20
  },
  submitRow: {
    paddingTop: 15
  },
  textArea: {
    height: 22
  },
  backgroundContainer: {
    backgroundColor: 'white',
    width: '100%',
    padding: 0,
    margin: 0
  }
};

export const POReceiptDataDisiplay: React.FC<Props> = ({ poReceipt }: Props): JSX.Element => {
  const { selectedMenu, stagedTable, packingSlipSubmissionLoading, clearPOSubmissionData } = useSelector((state: ReduxState) => state.poReceipts);

  const [packingSlip, setPackingSlip] = useState('');
  const [date, setDate] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [trackingNumbers, setTrackingNumbers] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();
  const [showModal, setShowModal] = useState<boolean>(false);

  const { selectedToDelete } = useSelector((state: ReduxState) => state.poReceipts);

  const onChangePackingSlip = (e: any): void => {
    const packingSlip = e.currentTarget.value as string;

    setPackingSlip(packingSlip.toUpperCase());
  };

  const onDateSelected = (_date: any, dateString: string): void => {
    setDate(dateString);
  };

  const onDeliveryMode = (value: string | string[]): void => {
    setDeliveryMode(value as string);
  };

  const onTrackingNumbers = (e: any) => {
    const trackingNumbers = e.currentTarget.value as string;

    setTrackingNumbers(trackingNumbers.toUpperCase());
  };

  const selectedSerialNumbers = selectedToDelete.map((line) => line.serialNumber);

  // question : we're storing receipt date but not sending it to api
  // same thing with delivery mode
  const onSubmitClicked = (): void => {
    const trimValue = trackingNumbers.replace(/\n/g, ' ').trim();
    const replaceSpace = trimValue.includes(' ') ? trimValue.replace(/ /g, ',') : trimValue;
    const trackingIds = replaceSpace.includes(',') ? replaceSpace.split(',').filter((value) => value.length) : [trimValue];
    const trackingIdsUppercase = trackingIds.map((id) => id.toUpperCase());

    const packingSlipNoWhitespace = packingSlip.replace(/\s+/g, '');

    let allTrackingIdsValid = true;
    let allTrackingNumberLengthsValid = true;
    let packingSlipValid = true;
    let packingSlipLengthValid = true;

    trackingIdsUppercase.forEach((id) => {
      if (!id.match(/^[a-z0-9]+$/i)) allTrackingIdsValid = false;
      else if (id.length > 30) allTrackingNumberLengthsValid = false;
    });

    const trackingIdDuplicates = trackingIdsUppercase.length === new Set(trackingIdsUppercase).size ? false : true;

    if (!packingSlipNoWhitespace.match(/^[a-z0-9]+$/i)) packingSlipValid = false;
    if (packingSlipNoWhitespace.length > 20) packingSlipLengthValid = false;

    if (!packingSlip.length) message.error('Must enter a value for Packing Slip');
    else if (!packingSlipValid) message.error('Packing Slip must be alpha-numeric only');
    else if (!packingSlipLengthValid) message.error('Packing slip length cannot exceed 20 characters');
    else if (!trackingNumbers.length) message.error('Must enter at least one valid tracking number');
    else if (!allTrackingIdsValid) message.error('Tracking numbers must be alpha-numeric only');
    else if (!allTrackingNumberLengthsValid) message.error('Tracking number length cannot exceed 30 characters');
    else if (trackingIdDuplicates) message.error('Duplicate tracking numbers not allowed');
    else {
      dispatch(
        asyncSubmitPackingSlip({
          getAccessTokenSilently,
          packingSlip: packingSlipNoWhitespace,
          productReceiptDate: date,
          deliveryMode: deliveryMode,
          trackingNumbers: trackingIdsUppercase
        })
      );
    }
  };

  const onDelete = (): void => {
    dispatch(removeSelectedRows(selectedToDelete));
    dispatch(setSelectedToDelete([]));
    dispatch(setSelectedKeysToDelete([]));
    dispatch(removeSelectedSerialNumbers(selectedSerialNumbers));
  };

  useEffect(() => {
    if (clearPOSubmissionData === true) {
      setPackingSlip('');
      setDeliveryMode('');
      setTrackingNumbers('');
      setDate('');
      dispatch(setClearPOSubmissionData(false));
      history.push('/');
    }
  }, [clearPOSubmissionData, dispatch, history]);

  return (
    <>
      <Row>
        <Row justify="space-between" style={{ background: 'rgb(206, 213, 242)', padding: 12, marginBottom: 10, width: '100%' }}>
          <Col>
            <AntPageTitle level={1} text="Purchase Order Details" />
          </Col>
          <Col>
            <Button danger disabled={!selectedToDelete.length} onClick={onDelete} style={{ marginRight: 5 }}>
              Delete
            </Button>
            <Button type="default" style={stagedTable.length ? { background: 'rgb(78, 147, 122)', color: 'white' } : undefined} disabled={!stagedTable.length} onClick={(): void => setShowModal(true)}>
              Post Line Items
            </Button>
          </Col>
        </Row>
        <Col style={styles.backgroundContainer}>
          <POHeader poReceipt={poReceipt} />
        </Col>
      </Row>
      <Modal okButtonProps={{ loading: packingSlipSubmissionLoading }} onOk={onSubmitClicked} okText="Submit" onCancel={(): void => setShowModal(false)} visible={showModal}>
        {packingSlipSubmissionLoading ? (
          <Row justify="center" align="middle" style={{ width: '100%', height: '20vh' }}>
            <Col>
              <BlockLoader direction="loader loader--slideUp" />
            </Col>
          </Row>
        ) : (
          <Col span={24} style={{ background: 'white', padding: 20, marginTop: 16 }}>
            <Row style={{ marginBottom: 16 }} justify="space-between" align="middle">
              <Col>
                <AntPageTitle level={1} text="Packing Slip Details" />
              </Col>
            </Row>
            <Row gutter={16} align="bottom">
              <Col span={24}>
                <Typography.Text>Packing Slip </Typography.Text>
                <Input style={{ marginBottom: 16 }} value={packingSlip} onChange={onChangePackingSlip}></Input>
                <Typography.Text>Product Receipt Date</Typography.Text>
                <DatePicker style={styles.datePicker} onChange={onDateSelected} value={date !== '' ? moment(date) : moment()} disabledDate={(currentDate): boolean => currentDate > moment()} />
              </Col>
              <Col span={24}>
                <Typography.Text style={{ marginTop: 16 }}>Delivery Mode </Typography.Text>
                <Tooltip title={'Coming in next release'}>
                  <Select allowClear style={styles.datePicker} onChange={onDeliveryMode} value={deliveryMode} disabled={true}>
                    <Select.Option value="Direct">Direct</Select.Option>
                    <Select.Option value="Indirect">Indirect</Select.Option>
                  </Select>
                </Tooltip>
                <Typography.Text style={{ marginTop: 16 }}>Tracking Number </Typography.Text>
                <TextArea style={styles.textArea} value={trackingNumbers} onChange={onTrackingNumbers}></TextArea>
              </Col>
            </Row>
          </Col>
        )}
      </Modal>
    </>
  );
};
